import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import SEO from '../../components/SEO';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../hooks/use-toast';
import {
  Camera,
  Upload,
  User,
  Building,
  Phone,
  Mail,
  Download,
  Lock,
  FileText,
  Scan,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Check,
  Edit,
  Globe,
  CreditCard
} from 'lucide-react';
import { Input } from '../../components/ui/input';

const STEPS = [
  { id: 1, title: 'Upload Card', icon: Camera },
  { id: 2, title: 'Verify Info', icon: Edit },
];

const BusinessCardScanner = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const fileInputRef = useRef(null);
  
  const [currentStep, setCurrentStep] = useState(1);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [extractedData, setExtractedData] = useState({
    name: '',
    title: '',
    company: '',
    email: '',
    phone: '',
    website: ''
  });
  const [showPreview, setShowPreview] = useState(false);
  const [showSignInPrompt, setShowSignInPrompt] = useState(false);

  // Swipe gesture state
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [touchStartX, setTouchStartX] = useState(null);
  const [touchEndX, setTouchEndX] = useState(null);
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchEndX(null);
    setTouchStart(e.targetTouches[0].clientY);
    setTouchStartX(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientY);
    setTouchEndX(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd || !touchStartX || !touchEndX) return;
    
    const distanceY = touchStart - touchEnd;
    const distanceX = touchStartX - touchEndX;
    
    const isHorizontalSwipe = Math.abs(distanceX) > Math.abs(distanceY);
    
    if (isHorizontalSwipe) {
      const isSwipeLeft = distanceX > minSwipeDistance;
      const isSwipeRight = distanceX < -minSwipeDistance;
      
      if (isSwipeLeft && !showPreview && currentStep < 2) {
        nextStep();
      } else if (isSwipeRight && !showPreview && currentStep > 1) {
        prevStep();
      }
    }
    // Removed swipe up/down for preview - only keep horizontal swipe for step navigation
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setUploadedImage(e.target.result);
        simulateOCR();
      };
      reader.readAsDataURL(file);
    }
  };

  const simulateOCR = () => {
    setIsProcessing(true);
    // Simulate processing delay
    setTimeout(() => {
      setExtractedData({
        name: 'John Doe',
        title: 'Senior Product Manager',
        company: 'Acme Corporation',
        email: 'john.doe@acme.com',
        phone: '+91 98765 43210',
        website: 'www.acme.com'
      });
      setIsProcessing(false);
      setCurrentStep(2);
      toast({ title: 'Scan Complete!', description: 'Contact information extracted.' });
    }, 2000);
  };

  const handleDataChange = (field, value) => {
    setExtractedData(prev => ({ ...prev, [field]: value }));
  };

  const nextStep = () => {
    if (currentStep === 1) {
      if (!uploadedImage) {
        toast({ title: 'Upload Required', description: 'Please upload a business card', variant: 'destructive' });
        return;
      }
      if (isProcessing) {
        toast({ title: 'Processing...', description: 'Please wait for the scan to complete' });
        return;
      }
      setCurrentStep(2);
    } else {
      setShowPreview(true);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(prev => prev - 1);
  };

  const handleSaveContact = () => {
    if (!user) {
      setShowSignInPrompt(true);
      return;
    }
    
    // Generate vCard
    const vcard = [
      'BEGIN:VCARD',
      'VERSION:3.0',
      `FN:${extractedData.name}`,
      `TITLE:${extractedData.title}`,
      `ORG:${extractedData.company}`,
      `EMAIL:${extractedData.email}`,
      `TEL:${extractedData.phone}`,
      `URL:${extractedData.website}`,
      'END:VCARD'
    ].join('\n');
    
    const blob = new Blob([vcard], { type: 'text/vcard' });
    const link = document.createElement('a');
    link.download = `${extractedData.name.replace(/\s/g, '_')}.vcf`;
    link.href = URL.createObjectURL(blob);
    link.click();
    toast({ title: 'Saved!', description: 'Contact saved as vCard.' });
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            <Label className="text-sm font-medium block">Upload Business Card</Label>
            <p className="text-xs text-gray-500 -mt-2">Take a photo or upload an image of a business card</p>
            
            <div
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                uploadedImage 
                  ? 'border-indigo-300 bg-indigo-50' 
                  : 'border-gray-300 hover:border-indigo-400 hover:bg-gray-50'
              }`}
            >
              {uploadedImage ? (
                <img src={uploadedImage} alt="Uploaded card" className="max-h-48 mx-auto rounded-lg shadow" />
              ) : (
                <>
                  <div className="w-16 h-16 mx-auto mb-4 bg-indigo-100 rounded-full flex items-center justify-center">
                    <Camera className="w-8 h-8 text-indigo-600" />
                  </div>
                  <p className="text-gray-600 mb-2">Click to upload or drag and drop</p>
                  <p className="text-sm text-gray-400">PNG, JPG up to 10MB</p>
                </>
              )}
            </div>
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
            />

            {isProcessing && (
              <div className="text-center">
                <div className="inline-flex items-center gap-3 px-4 py-2 bg-indigo-100 rounded-full">
                  <Scan className="w-5 h-5 text-indigo-600 animate-pulse" />
                  <span className="text-indigo-700 font-medium">Scanning card...</span>
                </div>
              </div>
            )}

            {uploadedImage && !isProcessing && (
              <Button
                variant="outline"
                onClick={() => {
                  setUploadedImage(null);
                  setExtractedData({
                    name: '', title: '', company: '', email: '', phone: '', website: ''
                  });
                  setCurrentStep(1);
                }}
                className="w-full"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload Different Card
              </Button>
            )}
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <Label className="text-sm font-medium block">Verify Extracted Information</Label>
            <p className="text-xs text-gray-500 -mt-2">Edit any fields that weren't scanned correctly</p>

            <div className="space-y-3">
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    value={extractedData.name}
                    onChange={(e) => handleDataChange('name', e.target.value)}
                    className="pl-10 h-11"
                    placeholder="Full Name"
                    data-testid="input-name"
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Title</Label>
                <div className="relative">
                  <FileText className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    value={extractedData.title}
                    onChange={(e) => handleDataChange('title', e.target.value)}
                    className="pl-10 h-11"
                    placeholder="Job Title"
                    data-testid="input-title"
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Company</Label>
                <div className="relative">
                  <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    value={extractedData.company}
                    onChange={(e) => handleDataChange('company', e.target.value)}
                    className="pl-10 h-11"
                    placeholder="Company"
                    data-testid="input-company"
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    value={extractedData.email}
                    onChange={(e) => handleDataChange('email', e.target.value)}
                    className="pl-10 h-11"
                    placeholder="Email"
                    data-testid="input-email"
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Phone</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    value={extractedData.phone}
                    onChange={(e) => handleDataChange('phone', e.target.value)}
                    className="pl-10 h-11"
                    placeholder="Phone"
                    data-testid="input-phone"
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Website</Label>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    value={extractedData.website}
                    onChange={(e) => handleDataChange('website', e.target.value)}
                    className="pl-10 h-11"
                    placeholder="Website"
                    data-testid="input-website"
                  />
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Layout>
      <SEO
        title="Free Business Card Scanner | OCR Card Reader Online"
        description="Scan business cards and extract contact information instantly. Free online business card scanner with OCR technology. Save contacts to your phone."
        keywords="business card scanner, OCR card reader, scan business card online, card to contact converter, free card scanner"
      />

      <div 
        className="min-h-screen bg-gray-50"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* Header */}
        <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
          <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Scan className="w-6 h-6 text-indigo-500" />
              <span className="font-bold text-lg text-gray-900">Card Scanner</span>
            </div>
            <button
              onClick={() => setShowPreview(true)}
              className="text-indigo-600 font-medium text-sm"
              data-testid="preview-button"
            >
              Save Contact
            </button>
          </div>
        </header>

        {/* Progress Steps */}
        <div className="bg-white border-b border-gray-100">
          <div className="max-w-lg mx-auto px-4 py-3">
            <div className="flex items-center justify-between">
              {STEPS.map((step, index) => (
                <div key={step.id} className="flex items-center">
                  <div className={`flex items-center gap-2 ${currentStep >= step.id ? 'text-indigo-600' : 'text-gray-400'}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                      currentStep > step.id ? 'bg-indigo-500 text-white' :
                      currentStep === step.id ? 'bg-indigo-100 text-indigo-600 border-2 border-indigo-500' :
                      'bg-gray-100 text-gray-400'
                    }`}>
                      {currentStep > step.id ? <Check className="w-4 h-4" /> : step.id}
                    </div>
                    <span className="text-sm font-medium hidden sm:inline">{step.title}</span>
                  </div>
                  {index < STEPS.length - 1 && (
                    <div className={`w-12 sm:w-24 h-0.5 mx-2 ${currentStep > step.id ? 'bg-indigo-500' : 'bg-gray-200'}`} />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <main className="max-w-lg mx-auto px-4 py-6 pb-32">
          <div className="mb-6">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
              {STEPS[currentStep - 1].title}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {currentStep === 1 && "Upload a photo of the business card"}
              {currentStep === 2 && "Review and edit the extracted details"}
            </p>
          </div>

          <Card className="shadow-sm border border-gray-100">
            <CardContent className="p-5">
              {renderStepContent()}
            </CardContent>
          </Card>

          {/* Note */}
          <p className="text-xs text-gray-400 text-center mt-4">
            Note: This is a demo. OCR extracts sample data.
          </p>
        </main>

        {/* Fixed Bottom Navigation */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-40">
          <div className="max-w-lg mx-auto px-4 py-4">
            <div className="flex items-center justify-between gap-4">
              {currentStep > 1 ? (
                <Button variant="outline" onClick={prevStep} className="flex-1 h-12" data-testid="back-button">
                  <ChevronLeft className="w-5 h-5 mr-1" /> Back
                </Button>
              ) : (
                <div className="flex-1" />
              )}
              
              <Button
                onClick={nextStep}
                disabled={currentStep === 1 && isProcessing}
                className="flex-1 h-12 bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white"
                data-testid="next-button"
              >
                {currentStep === 2 ? (
                  <>Save Contact</>
                ) : (
                  <>Next <ChevronRight className="w-5 h-5 ml-1" /></>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent 
          className="w-[95vw] max-w-md max-h-[90vh] overflow-y-auto p-0"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <div className="flex justify-center pt-2 pb-1">
            <div className="w-10 h-1 bg-gray-300 rounded-full" />
          </div>
          <p className="text-xs text-center text-gray-400 mb-2">Swipe down to close</p>
          
          <DialogHeader className="px-4 pb-3 border-b">
            <DialogTitle className="text-lg font-semibold text-center">Contact Preview</DialogTitle>
          </DialogHeader>
          
          <div className="p-4">
            {/* Contact Card Preview */}
            <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl p-5 mb-4 text-white">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center">
                  <User className="w-7 h-7" />
                </div>
                <div>
                  <div className="text-lg font-bold">{extractedData.name || 'Contact Name'}</div>
                  <div className="text-sm opacity-90">{extractedData.title || 'Job Title'}</div>
                </div>
              </div>
              
              <div className="space-y-2 text-sm">
                {extractedData.company && (
                  <div className="flex items-center gap-2 opacity-90">
                    <Building className="w-4 h-4" />
                    <span>{extractedData.company}</span>
                  </div>
                )}
                {extractedData.email && (
                  <div className="flex items-center gap-2 opacity-90">
                    <Mail className="w-4 h-4" />
                    <span>{extractedData.email}</span>
                  </div>
                )}
                {extractedData.phone && (
                  <div className="flex items-center gap-2 opacity-90">
                    <Phone className="w-4 h-4" />
                    <span>{extractedData.phone}</span>
                  </div>
                )}
                {extractedData.website && (
                  <div className="flex items-center gap-2 opacity-90">
                    <Globe className="w-4 h-4" />
                    <span>{extractedData.website}</span>
                  </div>
                )}
              </div>
            </div>

            <Button
              onClick={handleSaveContact}
              className="w-full h-12 bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white text-lg"
              data-testid="save-contact-button"
            >
              {user ? (
                <><Download className="w-5 h-5 mr-2" /> Save as Contact (.vcf)</>
              ) : (
                <><Lock className="w-5 h-5 mr-2" /> Sign In to Save</>
              )}
            </Button>

            {showSignInPrompt && !user && (
              <div className="mt-4 p-4 bg-indigo-50 rounded-xl border border-indigo-200">
                <p className="text-indigo-800 font-medium mb-3">
                  Create a free account to save contacts
                </p>
                <Button
                  onClick={() => navigate('/card-builder')}
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  Sign Up Free
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default BusinessCardScanner;
